import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    IconButton,
    Typography,
    Stack,
    Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PercentIcon from '@mui/icons-material/Percent';
import { addDays, differenceInDays, format, isSameDay, parseISO, startOfDay } from 'date-fns';

import type {
    ElasticSearchFilters,
    ElasticSearchHistogramResponse,
    ElasticSearchFrameIdData,
    PCAFrameDetails,
} from 'c-sdk';
import {
    ElasticSearchHistogramInterval,
    EsPCAReportField,
    EsPCAReportFilterField,
    PCAReportMetric,
} from 'c-sdk';

import { apiClient } from 'c-data';
import { useAPIClientRequest } from 'c-hooks';
import to from 'await-to-js';
import type {
    ReportConfig,
    ReportProps,
    ReportWrapperProps,
    TableReportHeader,
    TableReportRow,
    TableReportCell,
} from 'c-reports/Types';
import { ReportDateTimeframe, ReportType } from 'c-reports/Types';
import { ReportWrapper } from 'c-reports/Components';
import { useCommonTranslation } from 'c-translation';
import { isPlainObject } from '@reduxjs/toolkit';
import { esFrameDetailsToMySql } from 'c-main/Components/Campaign/CampaignReports/ElasticSearch/Transformers';
import FilterableFrameList from 'c-main/Components/Campaign/CampaignReports/Views/ByField/FieldFrameList/FilterableFrameList';
import { LoadingSpinner, TabsWrapper } from 'c-components';
import type { NetworkRequest } from 'c-data-layer';
import { NetworkRequestState } from 'c-data-layer';
import type { TabWrapperSettings } from 'c-types';

export type DrilldownKey = {
    field: EsPCAReportField;
    filterField: EsPCAReportFilterField;
    value: string;
    statsFilters?: ElasticSearchFilters;
};

type Props = {
    open: boolean;
    onClose: () => void;

    campaignId: number;
    environment: any;
    source?: any;

    startDate: Date;
    endDate: Date;

    fieldFilters?: ElasticSearchFilters;
    ignoredFilterKeys?: string[];

    geopathOnly?: boolean;
    showRoute?: boolean;
    audienceMode?: 'standard' | 'averages';
    averagesAvailable?: boolean;
    selectedAudienceLabel?: string | null;
    impressionsColumnLabel?: string;

    drilldown: DrilldownKey | null;

    title?: React.ReactNode;
};

const cell = (content: React.ReactNode, sx?: any): TableReportCell => ({ content, sx });

const fmtInt = (n?: number | null) => String(Math.round(n ?? 0));

const hours = Array.from({ length: 24 }, (_, i) => i);
const dayKey = (d: Date) => format(d, 'yyyy-MM-dd');
const histogramTabListProps = {
    variant: 'standard',
    centered: false,
} as const;
const DSP_AUDIENCE_FILTER_KEY = 'audience_display_name';

type HistogramValueSet = {
    impacts: number;
    plays: number;
    geopath: number;
    dsp: number;
    average: number;
};

type HistogramTabKey =
    | 'playsImpacts'
    | 'impactsAverage'
    | 'averagePlays'
    | 'audienceImpactsDsp'
    | 'audienceImpactsPlays'
    | 'audienceImpactsAverage';

type HistogramTabConfig = {
    key: HistogramTabKey;
    leftKey: keyof HistogramValueSet;
    rightKey: keyof HistogramValueSet;
    positiveWhen?: 'leftGteRight' | 'rightGteLeft';
    percentNumeratorKey?: keyof HistogramValueSet;
    percentDenominatorKey?: keyof HistogramValueSet;
};

const getDefaultHistogramTabKey = ({
    geopathOnly,
    audienceMode,
    selectedAudienceLabel,
}: {
    geopathOnly: boolean;
    audienceMode: 'standard' | 'averages';
    selectedAudienceLabel?: string | null;
}): HistogramTabKey => {
    if (geopathOnly && selectedAudienceLabel) return 'audienceImpactsDsp';
    if (geopathOnly) return 'playsImpacts';
    if (selectedAudienceLabel) return 'audienceImpactsDsp';
    if (audienceMode === 'averages') return 'impactsAverage';
    return 'playsImpacts';
};

const getAudienceImpactsLabel = (
    t: (path: string, options?: Record<string, any>) => string,
    selectedAudienceLabel?: string | null,
) =>
    selectedAudienceLabel
        ? `${selectedAudienceLabel} ${t('Modules.Main.Campaigns.Overview.Analytics.impactsTitle')}`
        : null;

const extractBuckets = (resp?: ElasticSearchHistogramResponse | null) => {
    const anyResp: any = resp as any;
    const data = anyResp?.data ?? anyResp?.reportData ?? anyResp?.report_data ?? anyResp?.buckets;
    if (!data) return [];

    if (typeof data === 'object' && !Array.isArray(data)) {
        return Object.entries(data).map(([ts, byKey]: any) => {
            const firstKey =
                byKey && typeof byKey === 'object' && !Array.isArray(byKey)
                    ? Object.keys(byKey)[0]
                    : null;
            const bucket = firstKey ? byKey[firstKey] : byKey;
            return { ts: String(ts), bucket };
        });
    }

    if (Array.isArray(data)) {
        return data.map((b: any, idx: number) => ({
            ts: String(b?.playout_start_datetime ?? b?.key ?? b?.name ?? idx),
            bucket: b,
        }));
    }

    return [];
};

const parseTs = (ts: string) => {
    // If your backend returns "2025-10-27 08:00:00.000", parseISO won’t like the space.
    // Convert to ISO-ish.
    const safe = ts.includes('T') ? ts : ts.replace(' ', 'T');
    return parseISO(safe);
};

const AnalyticsHourlyHistogramDialog: React.FC<Props> = ({
    open,
    onClose,
    campaignId,
    environment,
    source,
    startDate,
    endDate,
    fieldFilters,
    ignoredFilterKeys,
    geopathOnly = false,
    showRoute = false,
    audienceMode = 'standard',
    averagesAvailable = false,
    selectedAudienceLabel = null,
    impressionsColumnLabel,
    drilldown,
    title,
}) => {
    const t = useCommonTranslation();
    const openRef = useRef(open);
    const comparisonRequestIdRef = useRef(0);
    const audienceImpactsLabel = useMemo(
        () => getAudienceImpactsLabel(t, selectedAudienceLabel),
        [selectedAudienceLabel, t],
    );
    const defaultHistogramTabKey = useMemo(
        () => getDefaultHistogramTabKey({ geopathOnly, audienceMode, selectedAudienceLabel }),
        [audienceMode, geopathOnly, selectedAudienceLabel],
    );
    const [data, setData] = useState<ElasticSearchHistogramResponse | null>(null);
    const [comparisonData, setComparisonData] = useState<ElasticSearchHistogramResponse | null>(
        null,
    );
    const [currentStart, setCurrentStart] = useState<Date>(startDate);
    const [currentEnd, setCurrentEnd] = useState<Date>(endDate);
    const [selectedFrameId, setSelectedFrameId] = useState<string | null>(null);
    const [selectedTab, setSelectedTab] = useState<HistogramTabKey>(defaultHistogramTabKey);
    const [comparisonLoading, setComparisonLoading] = useState(false);
    const [showPercent, setShowPercent] = useState(false);
    const [histogramLoadingState, setHistogramLoadingState] = useState<NetworkRequestState>(
        NetworkRequestState.Idle,
    );
    const {
        start: startFrames,
        isLoading: framesLoading,
        data: framesData,
        reset,
    } = useAPIClientRequest(apiClient.ReportsElasticSearch.frameIdStats);

    useEffect(() => {
        openRef.current = open;
        if (!open) {
            comparisonRequestIdRef.current += 1;
            setSelectedFrameId(null);
            setSelectedTab(defaultHistogramTabKey);
            setComparisonData(null);
            setComparisonLoading(false);
            setShowPercent(false);
            setHistogramLoadingState(NetworkRequestState.Idle);
            setData(null);
            setCurrentStart(startDate);
            setCurrentEnd(endDate);
        }
    }, [defaultHistogramTabKey, endDate, open, startDate]);
    useEffect(
        () => () => {
            openRef.current = false;
            comparisonRequestIdRef.current += 1;
        },
        [],
    );
    const handleSelectFrameId = useCallback(
        (frameId: string | null) => {
            comparisonRequestIdRef.current += 1;
            setData(null);
            setComparisonData(null);
            setComparisonLoading(false);
            setShowPercent(false);
            setCurrentStart(startDate);
            setCurrentEnd(endDate);
            setHistogramLoadingState(NetworkRequestState.InProgress);
            setSelectedFrameId(frameId);
        },
        [endDate, startDate],
    );

    const effectiveDrilldown = useMemo<DrilldownKey | null>(() => {
        if (selectedFrameId) {
            return {
                field: EsPCAReportField.FrameId,
                filterField: EsPCAReportFilterField.FrameId,
                value: selectedFrameId,
            };
        }
        return drilldown;
    }, [drilldown, selectedFrameId]);

    const requestFilters = useMemo(() => {
        if (!effectiveDrilldown) return null;

        const merged = {
            ...(fieldFilters ?? {}),
            [effectiveDrilldown.filterField]: [String(effectiveDrilldown.value)],
        } as Record<string, string[]>;
        if (drilldown && selectedFrameId) {
            merged[drilldown.filterField] = [String(drilldown.value)];
        }

        // Histogram should not carry owner/format group filters from the leaderboard drilldown.
        delete merged[EsPCAReportFilterField.DisplayOwner];
        delete merged[EsPCAReportFilterField.DisplayFormat];
        delete merged[EsPCAReportFilterField.DisplayOwnerFormat];

        return merged;
    }, [effectiveDrilldown, fieldFilters, drilldown, selectedFrameId]);
    const isAuditedAudienceMode = audienceMode === 'standard' && Boolean(selectedAudienceLabel);
    const isGeopathAuditedMode = geopathOnly && isAuditedAudienceMode;
    const isTrueGeopathMode = geopathOnly && !isAuditedAudienceMode;

    const fetchData: ReportConfig<ElasticSearchHistogramResponse>['fetchData'] = useCallback(
        (s, e, timeframe) =>
            apiClient.ReportsElasticSearch.histogram({
                campaignId,
                interval: ElasticSearchHistogramInterval.Hour,
                startDate:
                    timeframe === ReportDateTimeframe.All
                        ? undefined
                        : `${format(s, 'yyyy-MM-dd')} 00:00:00`,
                endDate:
                    timeframe === ReportDateTimeframe.All
                        ? undefined
                        : `${format(e, 'yyyy-MM-dd')} 23:59:59`,
                field: effectiveDrilldown?.field ?? EsPCAReportField.FrameId,
                environment,
                source,
                fieldFilters: requestFilters,
            }) as any,
        [campaignId, effectiveDrilldown, environment, source, requestFilters],
    );

    const onDataUpdated: ReportConfig<ElasticSearchHistogramResponse>['onDataUpdated'] =
        useCallback(
            async (d, s, e, timeframe) => {
                setData(d);

                // keep stable refs unless changed
                setCurrentStart(curr => (!isSameDay(curr, s) ? s : curr));
                setCurrentEnd(curr => (!isSameDay(curr, e) ? e : curr));

                comparisonRequestIdRef.current += 1;
                const requestId = comparisonRequestIdRef.current;

                if (!isAuditedAudienceMode || isGeopathAuditedMode || !effectiveDrilldown) {
                    setComparisonData(null);
                    setComparisonLoading(false);
                    return;
                }

                const comparisonFilters = { ...(requestFilters ?? {}) };
                delete comparisonFilters[DSP_AUDIENCE_FILTER_KEY];
                delete comparisonFilters.audience_version;

                setComparisonLoading(true);
                const [comparisonErr, comparisonResp] = await to(
                    apiClient.ReportsElasticSearch.histogram({
                        campaignId,
                        interval: ElasticSearchHistogramInterval.Hour,
                        startDate:
                            timeframe === ReportDateTimeframe.All
                                ? undefined
                                : `${format(s, 'yyyy-MM-dd')} 00:00:00`,
                        endDate:
                            timeframe === ReportDateTimeframe.All
                                ? undefined
                                : `${format(e, 'yyyy-MM-dd')} 23:59:59`,
                        field: effectiveDrilldown.field,
                        environment,
                        source,
                        fieldFilters: comparisonFilters,
                    }) as any,
                );

                if (!openRef.current || requestId !== comparisonRequestIdRef.current) {
                    return;
                }

                setComparisonData(
                    comparisonErr
                        ? null
                        : ((comparisonResp ?? null) as ElasticSearchHistogramResponse | null),
                );
                setComparisonLoading(false);
            },
            [
                campaignId,
                effectiveDrilldown,
                environment,
                isAuditedAudienceMode,
                isGeopathAuditedMode,
                requestFilters,
                source,
            ],
        );

    const allDatesInRange = useMemo(() => {
        const s = startOfDay(currentStart ?? startDate);
        const e = startOfDay(currentEnd ?? endDate);
        const days = Math.max(0, differenceInDays(e, s));
        return Array.from({ length: days + 1 }, (_, i) => addDays(s, i));
    }, [currentStart, currentEnd, startDate, endDate]);

    const fetchFrames = useCallback(async () => {
        if (!drilldown) return;
        reset();
        const mergedFilters = {
            ...(fieldFilters ?? {}),
        } as Record<string, string[]>;

        if (drilldown.statsFilters != null) {
            delete mergedFilters[drilldown.filterField];
            Object.entries(drilldown.statsFilters).forEach(([filterKey, values]) => {
                if (values?.length) mergedFilters[filterKey] = values.map(String);
            });
        } else {
            mergedFilters[drilldown.filterField] = [String(drilldown.value)];
        }

        await to(
            startFrames({
                campaignId,
                environment,
                source,
                fieldFilters: mergedFilters,
                startDate: `${format(startDate, 'yyyy-MM-dd')} 00:00:00`,
                endDate: `${format(endDate, 'yyyy-MM-dd')} 23:59:59`,
            }),
        );
    }, [
        campaignId,
        drilldown,
        endDate,
        environment,
        fieldFilters,
        reset,
        source,
        startDate,
        startFrames,
    ]);

    useEffect(() => {
        if (!open) return;
        if (drilldown) fetchFrames();
        else reset();
    }, [drilldown, fetchFrames, open, reset]);

    const histogramTabs = useMemo<TabWrapperSettings['tabs']>(() => {
        if (isGeopathAuditedMode) {
            return [
                {
                    title: (
                        <>
                            {selectedAudienceLabel}{' '}
                            {t('Modules.Main.Campaigns.Overview.Analytics.impactsTitle')} /{' '}
                            {t('Modules.Main.Campaigns.Overview.Analytics.dspImpacts')}
                        </>
                    ),
                    urlName: 'audienceImpactsDsp',
                },
                {
                    title: (
                        <>
                            {selectedAudienceLabel}{' '}
                            {t('Modules.Main.Campaigns.Overview.Analytics.impactsTitle')} /{' '}
                            {t('Modules.Main.Campaigns.Overview.Analytics.playsTitle')}
                        </>
                    ),
                    urlName: 'audienceImpactsPlays',
                },
                ...(averagesAvailable
                    ? [
                          {
                              title: (
                                  <>
                                      {selectedAudienceLabel}{' '}
                                      {t('Modules.Main.Campaigns.Overview.Analytics.impactsTitle')}{' '}
                                      /{' '}
                                      {t(
                                          'Modules.Main.Campaigns.Overview.Analytics.audienceAverages',
                                      )}
                                  </>
                              ),
                              urlName: 'audienceImpactsAverage',
                          },
                      ]
                    : []),
                {
                    title: t('Modules.Main.Campaigns.Overview.Analytics.keyLabelImpactsPlays'),
                    urlName: 'playsImpacts',
                },
            ];
        }

        if (isTrueGeopathMode) {
            return [
                {
                    title: t(
                        'Modules.Main.Campaigns.Overview.Analytics.keyLabelImpressionsGeopath',
                    ),
                    urlName: 'playsImpacts',
                },
            ];
        }

        if (audienceMode === 'averages') {
            return [
                {
                    title: (
                        <>
                            {t('Modules.Main.Campaigns.Overview.Analytics.audienceAverages')} /{' '}
                            {t('Modules.Main.Campaigns.Overview.Analytics.impactsTitle')}
                        </>
                    ),
                    urlName: 'impactsAverage',
                },
                {
                    title: (
                        <>
                            {t('Modules.Main.Campaigns.Overview.Analytics.audienceAverages')} /{' '}
                            {t('Modules.Main.Campaigns.Overview.Analytics.playsTitle')}
                        </>
                    ),
                    urlName: 'averagePlays',
                },
                {
                    title: t('Modules.Main.Campaigns.Overview.Analytics.keyLabelImpactsPlays'),
                    urlName: 'playsImpacts',
                },
            ];
        }

        if (isAuditedAudienceMode) {
            return [
                {
                    title: (
                        <>
                            {selectedAudienceLabel}{' '}
                            {t('Modules.Main.Campaigns.Overview.Analytics.impactsTitle')} /{' '}
                            {t('Modules.Main.Campaigns.Overview.Analytics.dspImpacts')}
                        </>
                    ),
                    urlName: 'audienceImpactsDsp',
                },
                {
                    title: (
                        <>
                            {selectedAudienceLabel}{' '}
                            {t('Modules.Main.Campaigns.Overview.Analytics.impactsTitle')} /{' '}
                            {t('Modules.Main.Campaigns.Overview.Analytics.playsTitle')}
                        </>
                    ),
                    urlName: 'audienceImpactsPlays',
                },
                ...(averagesAvailable
                    ? [
                          {
                              title: (
                                  <>
                                      {selectedAudienceLabel}{' '}
                                      {t('Modules.Main.Campaigns.Overview.Analytics.impactsTitle')}{' '}
                                      /{' '}
                                      {t(
                                          'Modules.Main.Campaigns.Overview.Analytics.audienceAverages',
                                      )}
                                  </>
                              ),
                              urlName: 'audienceImpactsAverage',
                          },
                      ]
                    : []),
                {
                    title: t('Modules.Main.Campaigns.Overview.Analytics.keyLabelImpactsPlays'),
                    urlName: 'playsImpacts',
                },
            ];
        }

        return [
            {
                title: t('Modules.Main.Campaigns.Overview.Analytics.keyLabelImpactsPlays'),
                urlName: 'playsImpacts',
            },
        ];
    }, [
        audienceMode,
        averagesAvailable,
        isAuditedAudienceMode,
        isGeopathAuditedMode,
        isTrueGeopathMode,
        selectedAudienceLabel,
        t,
    ]);

    useEffect(() => {
        const firstTab =
            (histogramTabs[0]?.urlName as HistogramTabKey | undefined) ?? 'playsImpacts';
        setSelectedTab(currentTab =>
            histogramTabs.some(tab => tab.urlName === currentTab) ? currentTab : firstTab,
        );
    }, [histogramTabs]);
    useEffect(() => {
        setShowPercent(false);
    }, [selectedTab]);

    const activeTabConfig = useMemo<HistogramTabConfig>(() => {
        if (isGeopathAuditedMode && selectedTab === 'audienceImpactsDsp') {
            return {
                key: 'audienceImpactsDsp',
                leftKey: 'geopath',
                rightKey: 'impacts',
                positiveWhen: 'leftGteRight',
                percentNumeratorKey: 'geopath',
                percentDenominatorKey: 'impacts',
            };
        }

        if (isGeopathAuditedMode && selectedTab === 'audienceImpactsPlays') {
            return {
                key: 'audienceImpactsPlays',
                leftKey: 'geopath',
                rightKey: 'plays',
            };
        }

        if (isGeopathAuditedMode && selectedTab === 'audienceImpactsAverage') {
            return {
                key: 'audienceImpactsAverage',
                leftKey: 'geopath',
                rightKey: 'average',
                positiveWhen: 'leftGteRight',
                percentNumeratorKey: 'geopath',
                percentDenominatorKey: 'average',
            };
        }

        if (isTrueGeopathMode) {
            return {
                key: 'playsImpacts',
                leftKey: 'impacts',
                rightKey: 'geopath',
                positiveWhen: 'rightGteLeft',
            };
        }

        if (selectedTab === 'impactsAverage') {
            return {
                key: 'impactsAverage',
                leftKey: 'average',
                rightKey: 'impacts',
                positiveWhen: 'leftGteRight',
                percentNumeratorKey: 'average',
                percentDenominatorKey: 'impacts',
            };
        }

        if (selectedTab === 'averagePlays') {
            return {
                key: 'averagePlays',
                leftKey: 'average',
                rightKey: 'plays',
            };
        }

        if (selectedTab === 'audienceImpactsDsp') {
            return {
                key: 'audienceImpactsDsp',
                leftKey: 'impacts',
                rightKey: 'dsp',
                percentNumeratorKey: 'impacts',
                percentDenominatorKey: 'dsp',
            };
        }

        if (selectedTab === 'audienceImpactsPlays') {
            return {
                key: 'audienceImpactsPlays',
                leftKey: 'impacts',
                rightKey: 'plays',
            };
        }

        if (selectedTab === 'audienceImpactsAverage') {
            return {
                key: 'audienceImpactsAverage',
                leftKey: 'impacts',
                rightKey: 'average',
                positiveWhen: 'leftGteRight',
                percentNumeratorKey: 'impacts',
                percentDenominatorKey: 'average',
            };
        }

        return {
            key: 'playsImpacts',
            leftKey: 'impacts',
            rightKey: 'plays',
        };
    }, [isGeopathAuditedMode, isTrueGeopathMode, selectedTab]);
    const canShowPercentToggle = useMemo(
        () =>
            activeTabConfig.percentNumeratorKey != null &&
            activeTabConfig.percentDenominatorKey != null,
        [activeTabConfig],
    );
    const formatPercentValue = useCallback(
        (value: number) => {
            if (!Number.isFinite(value)) {
                return t('Modules.Main.Campaigns.Overview.Analytics.percentInfinity');
            }
            if (value !== 0 && Math.abs(value) < 0.5) return `${value.toFixed(2)}%`;
            return `${Math.round(value)}%`;
        },
        [t],
    );
    const percentToggleControl = useMemo(
        () =>
            canShowPercentToggle ? (
                <Tooltip
                    title={t(
                        showPercent
                            ? 'Modules.Main.Campaigns.Overview.Analytics.hidePercent'
                            : 'Modules.Main.Campaigns.Overview.Analytics.showPercent',
                    )}
                >
                    <IconButton
                        size="small"
                        onClick={() => setShowPercent(value => !value)}
                        sx={{
                            ml: 0.5,
                            color: showPercent ? 'primary.main' : undefined,
                            bgcolor: showPercent ? 'action.selected' : undefined,
                        }}
                    >
                        <PercentIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            ) : null,
        [canShowPercentToggle, showPercent, t],
    );

    // Build lookup: `${yyyy-MM-dd}:${hour}` -> values by metric pair
    const byDayHour = useMemo(() => {
        const buckets = extractBuckets(data);
        const comparisonBucketsByKey = extractBuckets(comparisonData).reduce((acc, item) => {
            acc[item.ts] = item.bucket;
            return acc;
        }, {} as Record<string, any>);

        return buckets.reduce((acc, { ts, bucket }) => {
            const d = parseTs(ts);
            if (Number.isNaN(d.getTime())) return acc;

            const dk = dayKey(d);
            const hr = Number(format(d, 'H'));

            const impacts = bucket?.impacts ?? bucket?.impacts_received_in_schedule ?? 0;
            const plays =
                bucket?.sum_of_plays ?? bucket?.plays ?? bucket?.plays_received_in_schedule ?? 0;
            const geopath = bucket?.geopath_audience ?? 0;
            const comparisonBucket = comparisonBucketsByKey[ts];
            const dsp =
                comparisonBucket?.dsp_impressions ??
                comparisonBucket?.delivered_impressions ??
                comparisonBucket?.impacts_received_in_schedule ??
                comparisonBucket?.impacts ??
                bucket?.dsp_impressions ??
                bucket?.delivered_impressions ??
                0;
            const average =
                comparisonBucket?.dsp_average_impressions ??
                comparisonBucket?.dsp_average_impacts ??
                bucket?.dsp_average_impressions ??
                bucket?.dsp_average_impacts ??
                0;

            acc[`${dk}:${hr}`] = { impacts, plays, geopath, dsp, average };
            return acc;
        }, {} as Record<string, HistogramValueSet>);
    }, [comparisonData, data]);

    const totalsByDay = useMemo(
        () =>
            allDatesInRange.reduce((acc, d) => {
                const dk = dayKey(d);
                const dayTotals = hours.reduce(
                    (t, hr) => {
                        const v = byDayHour[`${dk}:${hr}`];
                        if (!v) return t;
                        return {
                            impacts: t.impacts + (v.impacts ?? 0),
                            plays: t.plays + (v.plays ?? 0),
                            geopath: t.geopath + (v.geopath ?? 0),
                            dsp: t.dsp + (v.dsp ?? 0),
                            average: t.average + (v.average ?? 0),
                        };
                    },
                    { impacts: 0, plays: 0, geopath: 0, dsp: 0, average: 0 },
                );
                acc[dk] = dayTotals;
                return acc;
            }, {} as Record<string, HistogramValueSet>),
        [allDatesInRange, byDayHour],
    );

    const tableHeaders = useMemo<TableReportHeader[]>(
        () => [
            {
                content: '',
                sx: { position: 'sticky', left: 0, zIndex: 3, bgcolor: 'background.paper' },
            },
            ...allDatesInRange.map(d => ({
                content: (
                    <Box fontSize=".8em" lineHeight="1.2em">
                        <Box sx={{ textTransform: 'uppercase' }}>{format(d, 'EEE')}</Box>
                        <Box sx={{ whiteSpace: 'nowrap' }}>{format(d, 'do MMM')}</Box>
                    </Box>
                ),
                sx: { borderStyle: 'ridge' },
            })),
        ],
        [allDatesInRange],
    );

    const tableHeadersStickers = useMemo<TableReportHeader[]>(
        () => [
            { content: '' },
            ...allDatesInRange.map(day => {
                const weeks =
                    differenceInDays(startOfDay(day), startOfDay(startDate)) % 7 === 0 &&
                    differenceInDays(startOfDay(day), startOfDay(startDate)) >= 0;
                const isToday = differenceInDays(startOfDay(new Date()), startOfDay(day)) === 0;
                const weekNumber = 1 + differenceInDays(startOfDay(day), startOfDay(startDate)) / 7;

                const stickerSx = {
                    backgroundColor: isToday ? 'primary.main' : weeks ? 'grey.300' : '',
                    color: isToday ? 'white' : 'primary.main',
                    borderTopLeftRadius: '15px 15px',
                    borderTopRightRadius: '15px 15px',
                };

                return {
                    content: (
                        <Box display="flex" justifyContent="center" sx={stickerSx} fontSize="0.8em">
                            {isToday
                                ? t('Modules.Main.Campaigns.Overview.Reports.stickers.today')
                                : weeks
                                ? `${t(
                                      'Modules.Main.Campaigns.Overview.Reports.stickers.week',
                                  )} ${weekNumber}`
                                : ''}
                        </Box>
                    ),
                };
            }),
        ],
        [allDatesInRange, startDate, t],
    );

    const makeCellContent = useCallback(
        (v?: HistogramValueSet | null) => {
            if (!v) return <Typography variant="caption">-</Typography>;

            const left = v[activeTabConfig.leftKey] ?? 0;
            const right = v[activeTabConfig.rightKey] ?? 0;
            const numerator = activeTabConfig.percentNumeratorKey
                ? v[activeTabConfig.percentNumeratorKey] ?? 0
                : 0;
            const denominator = activeTabConfig.percentDenominatorKey
                ? v[activeTabConfig.percentDenominatorKey] ?? 0
                : 0;
            const percentValue =
                denominator > 0
                    ? (numerator / denominator) * 100
                    : numerator > 0
                    ? Number.POSITIVE_INFINITY
                    : 0;
            const textColor =
                activeTabConfig.positiveWhen === 'leftGteRight'
                    ? left >= right
                        ? 'success.main'
                        : 'error.main'
                    : activeTabConfig.positiveWhen === 'rightGteLeft'
                    ? right >= left
                        ? 'success.main'
                        : 'error.main'
                    : undefined;

            return (
                <Typography variant="caption" sx={{ fontWeight: 700 }} color={textColor} noWrap>
                    {showPercent && canShowPercentToggle
                        ? formatPercentValue(percentValue)
                        : `${fmtInt(left)} / ${fmtInt(right)}`}
                </Typography>
            );
        },
        [activeTabConfig, canShowPercentToggle, formatPercentValue, showPercent],
    );

    const tableRows = useMemo<TableReportRow[]>(() => {
        const overall: TableReportRow = {
            content: [
                cell(
                    <Typography variant="caption" sx={{ fontWeight: 700 }} noWrap>
                        {t('Modules.Main.Campaigns.Overview.Analytics.overall')}
                    </Typography>,
                    { position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 2 },
                ),
                ...allDatesInRange.map(d => {
                    const dk = dayKey(d);
                    return cell(makeCellContent(totalsByDay[dk]), { borderStyle: 'ridge' });
                }),
            ],
            sx: {
                '&:hover': { backgroundColor: 'action.hover' },
            },
        };

        const perHour = hours.map(
            hr =>
                ({
                    key: `hr-${hr}`,
                    content: [
                        cell(
                            <Typography variant="caption" sx={{ fontWeight: 700 }} noWrap>
                                {format(new Date(2000, 0, 1, hr), 'HH:00')}
                            </Typography>,
                            { position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 2 },
                        ),
                        ...allDatesInRange.map(d => {
                            const dk = dayKey(d);
                            const v = byDayHour[`${dk}:${hr}`];
                            return cell(makeCellContent(v ?? null), { borderStyle: 'ridge' });
                        }),
                    ],
                    sx: {
                        '&:hover': { backgroundColor: 'action.hover' },
                    },
                } as TableReportRow),
        );

        return [overall, ...perHour];
    }, [allDatesInRange, byDayHour, makeCellContent, t, totalsByDay]);

    const reportProps = useMemo<ReportProps<unknown>[]>(() => {
        const name = drilldown
            ? `${campaignId}_hourly_grid_${drilldown.value}`
            : `${campaignId}_hourly_grid`;
        return [
            {
                type: ReportType.Table,
                namePrefix: name,
                data: [],
                headerStickers: tableHeadersStickers,
                headers: tableHeaders,
                rows: tableRows,
                disableVirtualization: true,
                dense: true,
                controlsExtras: percentToggleControl,
            },
        ];
    }, [
        campaignId,
        drilldown,
        percentToggleControl,
        tableHeaders,
        tableHeadersStickers,
        tableRows,
    ]);

    const wrapperProps = useMemo<ReportWrapperProps<any>>(
        () => ({
            fetchData,
            onDataUpdated,
            initialStartDate: startDate,
            initialEndDate: endDate,
            removeLoadingBar: true,
            reportChartWrapperProps: { height: 520 },
            dateSettings: {
                timeframe: ReportDateTimeframe.All,
                minDate: startDate,
                maxDate: endDate,
                availableTimeframes: [
                    ReportDateTimeframe.Range,
                    ReportDateTimeframe.Weekly,
                    ReportDateTimeframe.Monthly,
                    ReportDateTimeframe.Daily,
                    ReportDateTimeframe.All,
                ],
            },
        }),
        [fetchData, onDataUpdated, startDate, endDate],
    );
    const keyLabel = useMemo(
        () =>
            isTrueGeopathMode
                ? audienceImpactsLabel
                    ? `${t(
                          'Modules.Main.Campaigns.Overview.Analytics.impactsTitle',
                      )} / ${audienceImpactsLabel}`
                    : showRoute
                    ? t('Modules.Main.Campaigns.Overview.Analytics.keyLabelImpressionsRoute')
                    : t('Modules.Main.Campaigns.Overview.Analytics.keyLabelImpressionsGeopath')
                : (fieldFilters?.display_type ?? []).some(
                      value => String(value).toLowerCase() === 'classic',
                  )
                ? t('Modules.Main.Campaigns.Overview.Analytics.keyLabelDisplayDaysImpressions')
                : t('Modules.Main.Campaigns.Overview.Analytics.keyLabelImpactsPlays'),
        [audienceImpactsLabel, fieldFilters?.display_type, isTrueGeopathMode, showRoute, t],
    );

    const formatDialogDate = useCallback((date?: Date | null) => {
        if (!date) return '-';
        return format(date, 'dd/MM/yyyy');
    }, []);
    const isAverageAudienceMode = audienceMode === 'averages';

    const frames = useMemo(() => {
        if (!isPlainObject(framesData?.data?.data)) {
            return [];
        }

        return Object.entries(framesData?.data?.data).reduce((acc, [frameId, details]) => {
            const mapped = esFrameDetailsToMySql(
                frameId,
                details as ElasticSearchFrameIdData,
                PCAReportMetric.Impacts,
            );
            const detailsWithAverage = details as ElasticSearchFrameIdData & {
                dsp_average_impressions?: number;
                dsp_average_impacts?: number;
            };
            const comparisonValue = geopathOnly
                ? (details as ElasticSearchFrameIdData)?.geopath_audience ?? 0
                : isAverageAudienceMode
                ? detailsWithAverage.dsp_average_impressions ??
                  detailsWithAverage.dsp_average_impacts ??
                  0
                : (details as ElasticSearchFrameIdData)?.sum_of_plays ?? 0;

            return [
                ...acc,
                {
                    ...mapped,
                    expected: comparisonValue,
                    percentage: mapped.actual > 0 ? (comparisonValue / mapped.actual) * 100 : 0,
                },
            ];
        }, [] as PCAFrameDetails[]);
    }, [framesData?.data?.data, geopathOnly, isAverageAudienceMode]);

    const selectedFrameData = useMemo(
        () => frames.find(frame => String(frame.frameId) === String(selectedFrameId)),
        [frames, selectedFrameId],
    );

    const handleCloseSelectedFrame = useCallback(() => {
        comparisonRequestIdRef.current += 1;
        setSelectedFrameId(null);
        setComparisonData(null);
        setComparisonLoading(false);
        setHistogramLoadingState(NetworkRequestState.Idle);
        setData(null);
        setCurrentStart(startDate);
        setCurrentEnd(endDate);
    }, [endDate, startDate]);

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="xl">
            <DialogTitle sx={{ pr: 6, position: 'relative' }}>
                <Box display="flex" alignItems="center" justifyContent="center">
                    <Typography variant="h4" sx={{ textAlign: 'center' }}>
                        {title ??
                            t('Modules.Main.Campaigns.Overview.Analytics.hourlyBreakdown', {
                                value: effectiveDrilldown?.value ?? '',
                            })}
                    </Typography>
                    <IconButton
                        onClick={onClose}
                        size="small"
                        sx={{
                            position: 'absolute',
                            right: 0,
                            top: '50%',
                            transform: 'translateY(-50%)',
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent dividers>
                <Box>
                    <Box position="relative" height={640}>
                        {framesLoading && (
                            <Box
                                position="absolute"
                                top={0}
                                left={0}
                                right={0}
                                bottom={0}
                                zIndex={1}
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                bgcolor="background.paper"
                            >
                                <LoadingSpinner />
                            </Box>
                        )}
                        <Box
                            sx={{
                                height: '100%',
                                visibility: framesLoading ? 'hidden' : 'visible',
                            }}
                        >
                            <FilterableFrameList
                                campaignId={campaignId}
                                field={drilldown?.field ?? EsPCAReportField.DisplayOwner}
                                frames={frames}
                                onSelect={handleSelectFrameId}
                                metric={PCAReportMetric.Impacts}
                                comparisonHeaderLabel={
                                    geopathOnly
                                        ? audienceImpactsLabel ??
                                          t(
                                              'Modules.Main.Campaigns.Overview.Analytics.geopathImpressionsLabel',
                                          )
                                        : isAverageAudienceMode
                                        ? t(
                                              'Modules.Main.Campaigns.Overview.Analytics.dspAverageImpacts',
                                          )
                                        : t('Modules.Main.Campaigns.Overview.Analytics.plays')
                                }
                                percentageHeaderLabel={
                                    geopathOnly
                                        ? showRoute
                                            ? t(
                                                  'Modules.Main.Campaigns.Overview.Analytics.routeImpactsPercentageLabel',
                                              )
                                            : t(
                                                  'Modules.Main.Campaigns.Overview.Analytics.geopathImpactsPercentageLabel',
                                              )
                                        : isAverageAudienceMode
                                        ? t(
                                              'Modules.Main.Campaigns.Overview.Analytics.performanceLabel',
                                          )
                                        : undefined
                                }
                                geopathOnly={geopathOnly}
                                showActualBeforeComparison={!(geopathOnly || isAverageAudienceMode)}
                                actualHeaderLabel={impressionsColumnLabel}
                            />
                        </Box>
                    </Box>
                </Box>
            </DialogContent>

            <DialogActions>
                <Box sx={{ flex: 1 }} />
            </DialogActions>

            <Dialog
                open={selectedFrameId != null}
                onClose={handleCloseSelectedFrame}
                fullScreen
                sx={{ mx: 2, my: 2 }}
            >
                <DialogTitle sx={{ pr: 6, position: 'relative' }}>
                    <Box display="flex" alignItems="center" justifyContent="center">
                        <Typography variant="h4" sx={{ textAlign: 'center' }}>
                            {t('Modules.Main.Campaigns.Overview.Analytics.hourlyBreakdown', {
                                value: selectedFrameData?.address ?? selectedFrameId ?? '',
                            })}
                        </Typography>
                        <IconButton
                            onClick={handleCloseSelectedFrame}
                            size="small"
                            sx={{
                                position: 'absolute',
                                right: 0,
                                top: '50%',
                                transform: 'translateY(-50%)',
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>
                    <Typography variant="body2" color="text.secondary" align="center">
                        {t('Modules.Main.Campaigns.Overview.Reports.frameID')}: {selectedFrameId}
                    </Typography>
                    <Box
                        display="flex"
                        justifyContent="space-evenly"
                        fontWeight={100}
                        sx={{
                            backgroundColor: 'action.hover',
                            borderRadius: '7px',
                            fontSize: '12px',
                            width: '85%',
                        }}
                        mt={2}
                        mx="auto"
                        p={1}
                    >
                        <Stack direction="row">
                            <Box fontWeight={500} pr={1}>
                                {t('Modules.Main.Campaigns.Overview.Reports.frameID')}:
                            </Box>
                            {`${selectedFrameId ?? '-'}`}
                        </Stack>
                        <Stack direction="row">
                            <Box fontWeight={500} pr={1}>
                                {t('Modules.Main.Campaigns.Overview.Reports.campaignDates')}:
                            </Box>
                            {`(${formatDialogDate(startDate)}) - (${formatDialogDate(endDate)})`}
                        </Stack>
                        <Stack direction="row">
                            <Box fontWeight={500} pr={1}>
                                {t('Modules.Main.Campaigns.Overview.Reports.pageDates')}:
                            </Box>
                            {`(${formatDialogDate(currentStart)}) - (${formatDialogDate(
                                currentEnd,
                            )})`}
                        </Stack>
                    </Box>
                </DialogTitle>

                <DialogContent dividers>
                    <Box position="relative" height="75vh" minHeight={0}>
                        {(histogramLoadingState === NetworkRequestState.InProgress ||
                            comparisonLoading) && (
                            <Box
                                position="absolute"
                                top={0}
                                left={0}
                                right={0}
                                bottom={0}
                                zIndex={1}
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                bgcolor="background.paper"
                            >
                                <LoadingSpinner />
                            </Box>
                        )}
                        <Box
                            sx={{
                                height: '100%',
                                minHeight: 0,
                                visibility:
                                    histogramLoadingState === NetworkRequestState.Success &&
                                    !comparisonLoading
                                        ? 'visible'
                                        : 'hidden',
                            }}
                        >
                            <Box display="flex" flexDirection="column" height="100%" minHeight={0}>
                                <TabsWrapper
                                    key={histogramTabs.map(tab => tab.urlName).join('|')}
                                    defaultOpenTab={selectedTab}
                                    tabs={histogramTabs}
                                    type="tertiary"
                                    tabListProps={histogramTabListProps as any}
                                    onTabChange={tab => setSelectedTab(tab as HistogramTabKey)}
                                />
                                <Box flex={1} overflow="auto" minHeight={0}>
                                    <ReportWrapper
                                        key={`${selectedFrameId}-${drilldown?.value ?? ''}`}
                                        {...wrapperProps}
                                        reportProps={reportProps}
                                        onLoadingStateUpdated={(state?: NetworkRequest) =>
                                            setHistogramLoadingState(
                                                state?.state ?? NetworkRequestState.Idle,
                                            )
                                        }
                                        wrapperProps={{
                                            sx: {
                                                display: 'flex',
                                                flexDirection: 'column',
                                                minHeight: 0,
                                            },
                                        }}
                                        reportWrapperProps={{
                                            sx: {
                                                flex: 1,
                                                overflow: 'visible',
                                                minHeight: 0,
                                            },
                                        }}
                                    />
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </DialogContent>
            </Dialog>
        </Dialog>
    );
};

export default AnalyticsHourlyHistogramDialog;
